import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { CheckCircle2, ArrowRight, Star, Phone } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import { FloatingWidgets } from "@/components/sections/FloatingWhatsApp";
import { TourCard } from "@/components/india/TourCard";
import { AwardsWall } from "@/components/AwardsWall";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { INFO_PAGES } from "@/data/pages";
import { TOURS, formatINR } from "@/data/tours";
import { TOUR_REVIEWS } from "@/data/indiaContent";
import { WORLD_REVIEWS } from "@/data/worldContent";
import { EBROCHURES, BRAND } from "@/data/content";
import { postLead } from "@/lib/leads";
import { resolveAssetPath } from "@/lib/assets";

const isGroup = (t) => /group/i.test(t.packageType || "");
const TOUR_SETS = {
  group: () => TOURS.filter(isGroup),
  economy: () => TOURS.filter((t) => t.startingPrice != null && t.startingPrice <= 40000).sort((a, b) => a.startingPrice - b.startingPrice),
  table: () => [...TOURS].sort((a, b) => a.title.localeCompare(b.title)),
  "group-table": () => TOURS.filter(isGroup).sort((a, b) => a.days - b.days),
};

const ToursTable = ({ tours }) => (
  <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl" data-testid="tours-table">
    <table className="w-full text-[13.5px] min-w-[640px]">
      <thead className="bg-[var(--navy)] text-white text-[12.5px]"><tr><th className="text-left px-4 py-3">Tour</th><th className="text-left px-4 py-3">Region</th><th className="px-4 py-3">Days</th><th className="text-right px-4 py-3">Starting price</th></tr></thead>
      <tbody className="divide-y divide-gray-100">
        {tours.map((t) => (
          <tr key={t.slug} className="hover:bg-[var(--soft)]">
            <td className="px-4 py-2.5"><Link to={`/tour/${t.slug}`} className="font-semibold text-[var(--navy)] hover:text-[var(--yellow-2)]">{t.title}</Link><span className="text-gray-400 text-[11px] ml-2">{t.code}</span></td>
            <td className="px-4 py-2.5 text-gray-600">{t.region}</td>
            <td className="px-4 py-2.5 text-center">{t.days}</td>
            <td className="px-4 py-2.5 text-right font-semibold">{formatINR(t.startingPrice)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const InfoForm = ({ form, pageTitle }) => {
  const [v, setV] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (v.name.trim().length < 2 || !/^\+?\d{10,13}$/.test(v.phone.replace(/[\s-]/g, ""))) return toast.error("Please enter your name and a valid mobile number.");
    if (form.messageLabel && v.message.trim().length < 5) return toast.error("Please add a few words in the message.");
    setBusy(true);
    try {
      await postLead({ ...v, destination: pageTitle, source: form.source });
      toast.success("Thank you! Our team will get back to you shortly.");
      setV({ name: "", phone: "", email: "", message: "" });
    } catch {
      toast.error("Couldn't submit right now. Please call +91-7052163551.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="bg-white border border-gray-200 rounded-2xl p-5 shadow-[0_20px_45px_-25px_rgba(11,37,69,0.5)] space-y-3" data-testid="info-form">
      <div className="font-serif-display text-xl text-[var(--navy)]">{form.cta}</div>
      <Input value={v.name} onChange={set("name")} placeholder="Full name*" data-testid="info-form-name" />
      <Input value={v.phone} onChange={set("phone")} placeholder="Mobile number*" data-testid="info-form-phone" />
      <Input value={v.email} onChange={set("email")} type="email" placeholder="Email (optional)" data-testid="info-form-email" />
      <Textarea value={v.message} onChange={set("message")} placeholder={form.messageLabel || "Message (optional)"} rows={3} data-testid="info-form-message" />
      <Button type="submit" disabled={busy} className="w-full bg-[var(--yellow)] hover:bg-[var(--yellow-2)] text-[var(--navy)] hover:text-white font-bold" data-testid="info-form-submit">{busy ? "Submitting..." : "Submit"}</Button>
    </form>
  );
};

const Sections = ({ page }) => (
  <div className="space-y-8">
    {(page.sections || []).map((s) => (
      <div key={s.h}>
        <h2 className="font-serif-display text-2xl text-[var(--navy)] mb-3">{s.h}</h2>
        {s.p && <p className="text-[14.5px] text-gray-600 leading-relaxed">{s.p}</p>}
        {s.list && <ul className="space-y-2">{s.list.map((l) => <li key={l} className="flex gap-2 text-[14.5px] text-gray-700"><CheckCircle2 className="w-4 h-4 text-[var(--yellow-2)] shrink-0 mt-1" />{l}</li>)}</ul>}
      </div>
    ))}
    {page.links && (
      <div className="grid sm:grid-cols-2 gap-3" data-testid="info-links">
        {page.links.map(([label, to]) => (
          <Link key={to} to={to} className="flex items-center justify-between bg-white border border-gray-200 rounded-xl px-4 py-3.5 font-semibold text-[var(--navy)] hover:border-[var(--yellow-2)] transition-colors">{label}<ArrowRight className="w-4 h-4 text-[var(--yellow-2)]" /></Link>
        ))}
      </div>
    )}
  </div>
);

const Testimonials = () => (
  <div className="grid md:grid-cols-2 gap-5" data-testid="testimonials-list">
    {[...TOUR_REVIEWS, ...WORLD_REVIEWS].map((r, i) => (
      <div key={i} className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex gap-0.5 mb-2">{Array.from({ length: r.rating || 5 }).map((_, k) => <Star key={k} className="w-4 h-4 fill-[var(--yellow)] text-[var(--yellow)]" />)}</div>
        <div className="font-semibold text-[var(--navy)]">{r.title}</div>
        <p className="text-[14px] text-gray-600 mt-1">“{r.text}”</p>
        <div className="text-[12px] text-gray-500 mt-3">{r.author} · {r.date}</div>
      </div>
    ))}
  </div>
);

export default function InfoPage() {
  const { slug } = useParams();
  const page = INFO_PAGES[slug];
  const tours = useMemo(() => (page?.tours ? TOUR_SETS[page.tours]() : []), [page]);
  if (!page) return <Navigate to="/" replace />;
  const wide = page.tours || page.testimonials || page.awards || page.brochures;
  return (
    <div className="bg-white text-[var(--navy)] overflow-x-hidden" data-testid={`info-page-${slug}`}>
      <Header />
      <section className="bg-[var(--navy)] text-white">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-12 md:py-16">
          <nav className="text-[12px] text-white/60 mb-4"><Link to="/" className="hover:text-white">Home</Link><span className="mx-1.5">&gt;</span><span className="text-white">{page.title}</span></nav>
          <div className="text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--yellow)]">{page.eyebrow}</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl mt-2" data-testid="info-page-title">{page.title}</h1>
          <p className="text-[15px] text-white/75 mt-4 max-w-2xl leading-relaxed">{page.intro}</p>
        </div>
      </section>
      {page.awards ? <AwardsWall heading={false} /> : (
        <section className="bg-[var(--soft)] py-12">
          <div className={`max-w-[1280px] mx-auto px-4 lg:px-6 grid gap-8 items-start ${page.form && !wide ? "lg:grid-cols-[1fr_360px]" : ""}`}>
            <div className="min-w-0 space-y-8">
              <Sections page={page} />
              {page.tours && (page.tours.includes("table") ? <ToursTable tours={tours} /> : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="info-tours">{tours.map((t, i) => <TourCard key={t.slug} tour={t} view="grid" index={i} />)}</div>
              ))}
              {page.testimonials && <Testimonials />}
              {page.brochures && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="brochure-list">
                  {EBROCHURES.map((b) => (
                    <div key={b.title} className="group bg-white rounded-xl overflow-hidden border border-gray-200">
                      <div className="aspect-[3/4] overflow-hidden"><img src={resolveAssetPath(b.image)} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" /></div>
                      <div className="p-3 font-semibold text-[13.5px]">{b.title}</div>
                    </div>
                  ))}
                </div>
              )}
              {page.form && wide && <div className="max-w-md"><InfoForm form={page.form} pageTitle={page.title} /></div>}
            </div>
            {page.form && !wide && <InfoForm form={page.form} pageTitle={page.title} />}
          </div>
        </section>
      )}
      <section className="bg-white py-10 border-t border-gray-100">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6 flex flex-wrap items-center justify-between gap-4">
          <div className="font-serif-display text-2xl">Need help planning? Talk to a travel expert.</div>
          <a href={`tel:${BRAND.phone}`} className="inline-flex items-center gap-2 bg-[var(--yellow)] hover:bg-[var(--yellow-2)] text-[var(--navy)] font-bold px-5 py-3 rounded-full transition-colors" data-testid="info-call-btn"><Phone className="w-4 h-4" /> {BRAND.phone}</a>
        </div>
      </section>
      <Footer />
      <FloatingWidgets />
    </div>
  );
}
